import { API_URL, getToken } from '../config';

/**
 * Audio Helper Utilities
 *
 * Utilities for converting audio between different formats:
 * - Blob ↔ Base64 (for database storage)
 * - Base64 ↔ URL (for playback)
 */

/**
 * Convert a Blob to Base64 string
 *
 * @param {Blob} blob - Audio blob from recorder
 * @returns {Promise<string>} Base64 encoded string (data URL)
 *
 * @example
 * const audioBlob = new Blob([...], { type: 'audio/webm' });
 * const base64 = await blobToBase64(audioBlob);
 * // Returns: "data:audio/webm;base64,GkXf..."
 */
export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      resolve(reader.result);
    };

    reader.onerror = (error) => {
      reject(new Error('Failed to convert blob to base64: ' + error));
    };

    reader.readAsDataURL(blob);
  });
};

/**
 * Convert Base64 string to Blob
 *
 * @param {string} base64 - Base64 encoded string (data URL)
 * @returns {Blob} Audio blob
 *
 * @example
 * const base64 = "data:audio/webm;base64,GkXf...";
 * const blob = base64ToBlob(base64);
 */
export const base64ToBlob = (base64) => {
  try {
    // Extract the base64 data and mime type
    const parts = base64.split(',');
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'audio/webm';
    const bstr = atob(parts[1]);

    // Convert to Uint8Array
    const n = bstr.length;
    const u8arr = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }

    return new Blob([u8arr], { type: mime });
  } catch (error) {
    console.error('Failed to convert base64 to blob:', error);
    return null;
  }
};

/**
 * Convert Base64 string to object URL for playback
 *
 * @param {string} base64 - Base64 encoded string (data URL)
 * @returns {string|null} Object URL for audio playback
 *
 * @example
 * const base64 = "data:audio/webm;base64,GkXf...";
 * const url = base64ToUrl(base64);
 * // Returns: "blob:http://localhost:3000/abc-123"
 */
export const base64ToUrl = (base64) => {
  const blob = base64ToBlob(base64);
  if (!blob) return null;
  return URL.createObjectURL(blob);
};

/**
 * Process audio for saving to database
 *
 * Handles both Blob and base64 string inputs
 *
 * @param {Blob|string|null} audio - Audio data (blob or base64)
 * @returns {Promise<string|null>} Base64 string or null
 *
 * @example
 * // With blob
 * const base64 = await processAudioForSave(audioBlob);
 *
 * // With base64 (already converted)
 * const base64 = await processAudioForSave(existingBase64);
 *
 * // With null
 * const base64 = await processAudioForSave(null); // Returns null
 */
export const processAudioForSave = async (audio) => {
  if (!audio) return null;

  // Already a URL path (from REST upload) - pass through
  if (typeof audio === 'string' && (audio.startsWith('/') || audio.startsWith('http'))) {
    return audio;
  }

  // Already base64
  if (typeof audio === 'string' && audio.startsWith('data:')) {
    return audio;
  }

  // Blob - convert to base64
  if (audio instanceof Blob) {
    return await blobToBase64(audio);
  }

  return null;
};

/**
 * Process audio examples array for saving
 *
 * Converts all audio blobs in examples to base64
 *
 * @param {Array} meanings - Array of meanings with examples
 * @returns {Promise<Array>} Meanings with base64 audio
 *
 * @example
 * const meanings = [
 *   {
 *     definition: "hello",
 *     examples: [
 *       { cn: "你好", en: "Hello", audio: audioBlob }
 *     ]
 *   }
 * ];
 * const processed = await processExamplesForSave(meanings);
 */
export const processExamplesForSave = async (meanings) => {
  if (!meanings || !Array.isArray(meanings)) return [];

  const processedMeanings = [];

  for (const meaning of meanings) {
    const processedExamples = [];

    if (meaning.examples && Array.isArray(meaning.examples)) {
      for (const example of meaning.examples) {
        const processedAudio = await processAudioForSave(example.audio);
        processedExamples.push({
          ...example,
          audio: processedAudio
        });
      }
    }

    processedMeanings.push({
      ...meaning,
      examples: processedExamples
    });
  }

  return processedMeanings;
};

/**
 * Get audio size in KB
 *
 * @param {string} base64 - Base64 encoded audio
 * @returns {number} Size in kilobytes
 */
export const getAudioSize = (base64) => {
  if (!base64 || typeof base64 !== 'string') return 0;

  // Remove data URL prefix
  const base64Data = base64.split(',')[1] || base64;

  // Calculate size (base64 is ~33% larger than binary)
  const sizeInBytes = (base64Data.length * 3) / 4;
  return Math.round(sizeInBytes / 1024); // KB
};

/**
 * Validate audio size
 *
 * @param {string} base64 - Base64 encoded audio
 * @param {number} maxSizeKB - Maximum size in KB (default: 1024 = 1MB)
 * @returns {boolean} True if valid
 */
export const validateAudioSize = (base64, maxSizeKB = 1024) => {
  const sizeKB = getAudioSize(base64);
  return sizeKB <= maxSizeKB;
};

/**
 * Get the API base URL from centralized config
 */
const getApiBaseUrl = () => API_URL;

/**
 * Get the full audio URL from a path
 *
 * @param {string} audioPath - Audio path (e.g., "/audio/abc123.webm")
 * @returns {string} Full URL for audio playback
 */
export const getAudioUrl = (audioPath) => {
  if (!audioPath) return '';

  // If already a full URL, return as-is
  if (audioPath.startsWith('http://') || audioPath.startsWith('https://')) {
    return audioPath;
  }

  // If it's a data URL (base64), return as-is
  if (audioPath.startsWith('data:')) {
    return audioPath;
  }

  // Build full URL from path
  return `${getApiBaseUrl()}${audioPath}`;
};

/**
 * Upload audio file to backend
 *
 * @param {string} wordId - The word ID
 * @param {Blob} audioBlob - The audio blob to upload
 * @returns {Promise<Object>} Response with audio URL
 */
export const uploadAudio = async (wordId, audioBlob) => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');

  const headers = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${getApiBaseUrl()}/api/audio/${wordId}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Upload failed');
  }

  return response.json();
};

/**
 * Delete audio file from backend
 *
 * @param {string} wordId - The word ID
 * @returns {Promise<Object>} Response
 */
export const deleteAudio = async (wordId) => {
  const headers = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${getApiBaseUrl()}/api/audio/${wordId}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Delete failed');
  }

  return response.json();
};
